import { useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Send, TrendingDown, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";

const SUGGESTIONS = [
  "Which branch has the lowest doctor consultation rating?",
  "Show me all dues overdue by 30+ days",
  "How many post-op follow-ups did we miss?",
  "Summarise open concerns by severity",
];

export default function AIAssistant({ open, onClose }) {
  const { branch } = useApp();
  const [question, setQuestion] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [risks, setRisks] = useState(null);
  const [risksLoading, setRisksLoading] = useState(false);
  const [summary, setSummary] = useState("");
  const [sumLoading, setSumLoading] = useState(false);
  const [period, setPeriod] = useState("weekly");

  const ask = async (q) => {
    const query = q || question;
    if (!query.trim()) return;
    setMessages((m) => [...m, { role: "user", text: query }]);
    setQuestion("");
    setLoading(true);
    try {
      const res = await api.post("/ai/query", { question: query, branch });
      setMessages((m) => [...m, { role: "ai", text: res.data.answer }]);
    } catch {
      toast.error("AI query failed");
      setMessages((m) => [...m, { role: "ai", text: "Sorry, I couldn't process that. Try again." }]);
    } finally {
      setLoading(false);
    }
  };

  const loadRisks = async () => {
    setRisksLoading(true);
    try {
      const res = await api.get("/ai/churn-risk", { params: { branch } });
      setRisks(res.data.risks);
    } catch { toast.error("Failed to load risk scores"); }
    finally { setRisksLoading(false); }
  };

  const loadSummary = async () => {
    setSumLoading(true);
    try {
      const res = await api.get("/ai/summary", { params: { period, branch } });
      setSummary(res.data.summary);
    } catch { toast.error("Failed to generate summary"); }
    finally { setSumLoading(false); }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-xl bg-[#0F172A] border-l border-white/10 text-white p-0 flex flex-col">
        <SheetHeader className="p-5 border-b border-white/5">
          <SheetTitle className="text-white flex items-center gap-2 font-display font-light text-2xl">
            <Sparkles className="h-5 w-5 text-[#C9A24B]" /> Patient Experience Intelligence
          </SheetTitle>
        </SheetHeader>

        <Tabs defaultValue="query" className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="mx-5 mt-4 bg-[#1E293B] border border-white/5">
            <TabsTrigger value="query" data-testid="ai-tab-query" className="data-[state=active]:bg-[#C9A24B] data-[state=active]:text-[#0F172A]">Ask</TabsTrigger>
            <TabsTrigger value="risk" data-testid="ai-tab-risk" className="data-[state=active]:bg-[#C9A24B] data-[state=active]:text-[#0F172A]">Churn Risk</TabsTrigger>
            <TabsTrigger value="summary" data-testid="ai-tab-summary" className="data-[state=active]:bg-[#C9A24B] data-[state=active]:text-[#0F172A]">Summary</TabsTrigger>
          </TabsList>

          {/* Query */}
          <TabsContent value="query" className="flex-1 flex flex-col overflow-hidden m-0 p-5 pt-4">
            <div className="flex-1 overflow-y-auto space-y-3 mb-4">
              {messages.length === 0 && (
                <div className="space-y-2">
                  <p className="text-slate-400 text-sm mb-3">Ask anything about your patients, dues, feedback or concerns.</p>
                  {SUGGESTIONS.map((s) => (
                    <button key={s} data-testid="ai-suggestion" onClick={() => ask(s)}
                      className="w-full text-left text-sm p-3 rounded-lg bg-[#1E293B] border border-white/5 text-slate-300 hover:border-[#C9A24B]/40 transition-colors">
                      {s}
                    </button>
                  ))}
                </div>
              )}
              {messages.map((m, i) => (
                <div key={i} className={`p-3 rounded-lg text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === "user" ? "bg-[#C9A24B]/15 text-white ml-8" : "bg-[#1E293B] text-slate-200 mr-4 border border-white/5"
                }`}>{m.text}</div>
              ))}
              {loading && <div className="flex items-center gap-2 text-slate-400 text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Thinking…</div>}
            </div>
            <div className="flex gap-2">
              <Input data-testid="ai-query-input" value={question} onChange={(e) => setQuestion(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && ask()}
                placeholder="Ask a question…" className="bg-[#1E293B] border-white/10 text-white" />
              <Button data-testid="ai-query-send" onClick={() => ask()} disabled={loading} className="bg-[#C9A24B] text-[#0F172A] hover:bg-[#b8923f]">
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </TabsContent>

          {/* Risk */}
          <TabsContent value="risk" className="flex-1 overflow-y-auto m-0 p-5 pt-4">
            <Button data-testid="ai-load-risk" onClick={loadRisks} disabled={risksLoading} className="w-full mb-4 bg-[#1E293B] border border-white/10 hover:bg-[#1E293B]/70">
              {risksLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <TrendingDown className="h-4 w-4 mr-2" />} Scan Dissatisfaction Risk
            </Button>
            <div className="space-y-2">
              {risks?.length === 0 && <p className="text-slate-500 text-sm text-center py-6">No at-risk patients detected.</p>}
              {risks?.map((r) => (
                <div key={r.patient_id} className="p-3 rounded-lg bg-[#1E293B] border border-white/5">
                  <div className="flex items-center justify-between">
                    <span className="text-white text-sm font-medium">{r.patient_name}</span>
                    <Badge className={`border-0 ${r.level === "High" ? "bg-[#DC2626]/20 text-[#f87171]" : r.level === "Medium" ? "bg-[#C9A24B]/20 text-[#C9A24B]" : "bg-[#64748B]/20 text-slate-300"}`}>
                      {r.risk_score}% · {r.level}
                    </Badge>
                  </div>
                  <div className="text-xs text-slate-400 mt-1">{r.branch} · {r.reasons.join(", ")}</div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Summary */}
          <TabsContent value="summary" className="flex-1 overflow-y-auto m-0 p-5 pt-4">
            <div className="flex gap-2 mb-4">
              {["weekly", "monthly"].map((p) => (
                <button key={p} data-testid={`ai-period-${p}`} onClick={() => setPeriod(p)}
                  className={`px-3 py-1.5 rounded-md text-sm capitalize ${period === p ? "bg-[#C9A24B] text-[#0F172A]" : "bg-[#1E293B] text-slate-300"}`}>{p}</button>
              ))}
              <Button data-testid="ai-generate-summary" onClick={loadSummary} disabled={sumLoading} className="ml-auto bg-[#C9A24B] text-[#0F172A] hover:bg-[#b8923f]">
                {sumLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileText className="h-4 w-4 mr-2" />} Generate
              </Button>
            </div>
            {summary ? (
              <div className="p-4 rounded-lg bg-[#1E293B] border border-white/5 text-sm text-slate-200 whitespace-pre-wrap leading-relaxed">{summary}</div>
            ) : (
              <p className="text-slate-500 text-sm text-center py-6">Generate an auto-drafted experience report for the Founder.</p>
            )}
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}
